import { ContextualMenu, IconTrash, IconUpload, MomentObj, OptionInterface, OptionValue, Search, SelectOption, TableColumns, TableColumnWidth, TableData, TablePagination, TablePreferences } from "@veneer/core";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import justificationAbsencesService from "../../services/userManagementApi/justificationAbsences.service";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useTranslation } from "react-i18next";
import { IFormUpdateValues, IJustificationAbsenceDto, IJustificationAbsencesAndCountsDto, IJustificationAbsenceTypesDto, initialFormUpdateValues, initialValuesSearch, IPropsComponentesTab, IUpdateJustificationAbsenceDto, IValuesGraph, TableColumnsAbsences } from "../../models/dto/justificationAbsences/IJustificationAbsenceModelDto";

const initialRangeDate = {
    startDate: moment().clone().subtract(30, 'days'),
    endDate: moment().clone()
};

export const useViewJustifications = (props: IPropsComponentesTab) => {
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [t] = useTranslation("global");
  
  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [ startDate, setStartDate ] = useState< MomentObj | undefined>(initialRangeDate.startDate);
  const [ endDate, setEndDate ] = useState<MomentObj | undefined>(initialRangeDate.endDate);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [tableFilteredData, setTableFilteredData] = useState<TableData[]>([]);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [modalUpdateOpen, setModalUpdateOpen] = useState<boolean>(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [optionsJustificationAbsence, setOptionsJustificationAbsence] = useState<SelectOption[]>([]);
  const [valueJustificationAbsenceDelete, setValueJustificationAbsenceDelete] = useState<number>(0);
  const [disabledBtnDownloadReport, setDisabledBtnDownloadReport] = useState<boolean>(false);
  const [valuesGraph, setValuesGraphic] = useState<IValuesGraph>({
    totalAbsencesCount: 0,
    totalJustificationsCount: 0
  });
  const [valueSearch, setValueSearch] = useState<TableColumnsAbsences>({
    ...initialValuesSearch,
  });
  const [formValuesUpdate, setFormValuesUpdate] = useState<IFormUpdateValues>({
    ...initialFormUpdateValues,
  });

  const labelsResponseUpdateJustificationAbsence = [
    t('modal.unexpectedError'), // 0
    t('modal.justificationAbsenceUpdated'), // 1
    t('modal.errorData'), // 2
    t('modal.justificationAbsenceNotFound'), // 3
    t('modal.justificationAbsenceTypeNotFound'), // 4
    t('modal.informationNotUpdated'), // 5
  ]

  const labelsResponseDeleteJustificationAbsence = [
    t('modal.unexpectedError'), // 0
    t('modal.justificationAbsenceDeleted'), // 1
    t('modal.errorData'), // 2
    t('modal.justificationAbsenceNotFound'), // 3
    t('modal.informationNotUpdated'), // 4
  ]

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    option: OptionInterface<number>
  ) => {
    setPageSize(option.value);
  };

  const paginationProps: TablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: tableFilteredData.length,
  };  

  const chartData = {
    labels: [t("modal.justified"), t("modal.unjustified")],
    datasets: [
      {
        data: [
          valuesGraph.totalJustificationsCount,
          valuesGraph.totalAbsencesCount
        ],
        backgroundColor: [
          "#A9DFBF", // registered
          "#FFCDD2", // Unregistered
        ],
        hoverOffset: 5
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'right' as 'right',
        title: {
          display: true,
          text: t('modal.PosWithoutCheckIn'),
        }
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let value = context.raw || 0;
            let total =
              context.dataset.data.reduce((acc: any, curr: any) => acc + curr, 0);
            let percentage = (value / total * 100).toFixed(2) + '%';
            return ` ${value} (${percentage})`;
          }
        }
      }
    }
  };

  const resetSearch = () => {
    setValueSearch({ ...initialValuesSearch });
  };

  const handleFilterChange = (field: keyof TableColumnsAbsences, value: string) => {
    if (value === "") {
      resetSearch();
      setTableFilteredData(tableData);
      return;
    }
    setValueSearch((prevState) => {
      const updatedSearchValues: TableColumnsAbsences = {
        ...prevState,
        [field]: value,
      };

      (Object.keys(prevState) as (keyof TableColumnsAbsences)[]).forEach((key) => {
        if (key !== field) {
          updatedSearchValues[key] = "";
        }
      });

      return updatedSearchValues;
    });

    const lowerCaseValue = value.toLowerCase();
    const filterArray = tableData.filter((element: any) => {
      const elementValue = element[field]?.toString().toLowerCase() || "";
      return elementValue.includes(lowerCaseValue);
    });

    setTableFilteredData(filterArray);
    setCurrentPage(1);
  }

  const columnsWidth: TableColumnWidth[] = [
    { columnId: "advisor", width: "25%" },
    { columnId: "posCode", width: "20%" },
    { columnId: "scheduledAppointmentDate", width: "15%" },
    { columnId: "justificationAbsenceType", width: "20%" },
    { columnId: "creationDate", width: "10%" },
    { columnId: "modificationDate", width: "10%" },
  ];

  const tablePreferences: TablePreferences = {
    width: columnsWidth,
  };

  const columns: TableColumns[] = [
    {
      id: "advisor",
      label: t("header.name"),
      filter: (
        <div style={{ width: "100%" }}>
          <Search
            label={`${t("header.search")} ${t("header.name")}`}
            placeholder={`${t("header.search")} ${t("header.name")}`}
            id={`table-filter`}
            onClear={() => {}}
            value={valueSearch.advisor}
            onChange={(e) => handleFilterChange("advisor", e!.toString())}
          />
        </div>
      ),
    },
    {
      id: "posCode",
      label: t("header.pointOfSaleCode"),
      filter: (
        <div style={{ width: "100%" }}>
          <Search
            label={`${t("header.search")} ${t("header.pointOfSaleCode")}`}
            placeholder={`${t("header.search")} ${t("header.pointOfSaleCode")}`}
            id={`table-filter`}
            onClear={() => {}}
            value={valueSearch.posCode}
            onChange={(e) => handleFilterChange("posCode", e!.toString())}
          />
        </div>
      ),
    },
    {
      id: "scheduledAppointmentDate",
      label: t("header.visitDate"),
      filter: (
        <div style={{ width: "100%" }}>
          <Search
            label={`${t("header.search")} ${t("header.visitDate")}`}
            placeholder={`${t("header.search")} ${t("header.visitDate")}`}
            id={`table-filter`}
            onClear={() => {}}
            value={valueSearch.scheduledAppointmentDate}
            onChange={(e) => handleFilterChange("scheduledAppointmentDate", e!.toString())}
          />
        </div>
      ),
    },
    {
      id: "justificationAbsenceType",
      label: t("header.justificationAbsence"),
      filter: (
        <div style={{ width: "100%" }}>
          <Search
            label={`${t("header.search")} ${t("header.justificationAbsence")}`}
            placeholder={`${t("header.search")} ${t("header.justificationAbsence")}`}
            id={`table-filter`}
            onClear={() => {}}
            value={valueSearch.justificationAbsenceType}
            onChange={(e) => handleFilterChange("justificationAbsenceType", e!.toString())}
          />
        </div>
      ),
    },
    { id: "creationDate", label: t("header.createdDate") },
    { id: "modificationDate", label: t("header.modifiedDate") },
  ];

  const options: OptionInterface[] = useMemo(() => [
    { value: 1, label: t("header.update"), icon: <IconUpload /> },
    { value: 2, label: t("header.delete"), icon: <IconTrash /> },
  ], [t]);

  const contextualMenu = useCallback((
    userID: number,
    advisorName: string,
    posCode: string,
    scheduledAppointmentDate: string,
    justificationAbsenceTypeID: number, 
    idJustificationAbsence: number
  ) => (
    <ContextualMenu
      onClick={(e: any, option?: OptionInterface<OptionValue> | undefined) => {
        if (moment(scheduledAppointmentDate, 'DD/MM/YYYY').isBefore(moment().clone().subtract(2, 'days'), 'day')){
          props.setOpenModalMessage(true);
          props.setMessageModalMessage(t('modal.editJustificationDisabled'));
          return;
        }

        if (option?.value === 1) { // update

          setFormValuesUpdate({
            advisor: advisorName,
            advisorID: userID,
            posCode: posCode,
            idJustificationAbsence: idJustificationAbsence,
            scheduledAppointmentDate: scheduledAppointmentDate,
            justificationAbsenceType: [justificationAbsenceTypeID]
          });
          setModalUpdateOpen(true);

        } else if (option?.value === 2) { // delete

          setValueJustificationAbsenceDelete(idJustificationAbsence);
          setDeleteModal(true);
        }
      }}
      options={options}
    />
  ), [options, props, t]);

  const pagedData = tableFilteredData.slice(
    (currentPage - 1) * pageSize,
    (currentPage - 1) * pageSize + pageSize
  );

  const fillData = useCallback((data: IJustificationAbsenceDto[]) => {
    const pagedData: TableData[] = [];
    for (let i = 0; i < data.length; i++) {
      const absence: IJustificationAbsenceDto = data[i];
      const tableData: TableData = {
        userID: absence.advisorID,
        advisor: absence.advisor,
        posCode: absence.posCode,
        scheduledAppointmentDate: absence.scheduledAppointmentDate,
        justificationAbsenceType: absence.justificationAbsenceType,
        deleted: absence.deleted ? t("header.yes") : t("header.no"),
        creationDate: absence.creationDate,
        modificationDate: absence.modificationDate ?? '',
        rowConfig: {
          action: contextualMenu(
            absence.advisorID,
            absence.advisor,
            absence.posCode,
            absence.scheduledAppointmentDate,
            absence.justificationAbsenceTypeID,
            absence.idJustificationAbsence,
          ),
          noWrap: false
        },
      };
      pagedData.push(tableData);
    }
    setTableFilteredData(pagedData);
    setTableData(pagedData);
  }, [contextualMenu, t]);  

  const getJustificationAbsences = useCallback((_languageID: number) => {
    
    props.setLoading(true);
    const range = { startDate: startDate ?? initialRangeDate.startDate, endDate: endDate ?? initialRangeDate.endDate  }
    justificationAbsencesService.GetJustificationAbsences(_languageID, range.startDate.format("MM/DD/YYYY"), range.endDate.format("MM/DD/YYYY"))
    .then((result: IJustificationAbsencesAndCountsDto) => {
      fillData(result.justifications);
      setDisabledBtnDownloadReport(result.justifications.length === 0);
      setValuesGraphic({
        totalAbsencesCount: result.totalAbsencesCount,
        totalJustificationsCount: result.totalJustificationsCount
      });
      props.setLoading(false);
    })
    .catch((error) => {
      console.log(error);
      props.setLoading(false);
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillData, props.setLoading, startDate, endDate]);

  const onChangeJustificationAbsenceUpdate = (optionSelected: SelectOption) => {
    setFormValuesUpdate((prevState: any) => ({
      ...prevState,
      justificationAbsenceType: [optionSelected.value]
    }));
  }

  const getJustificationAbsenceTypes = useCallback((_languageID: number) => {
    props.setLoading(true);
    justificationAbsencesService.GetJustificationAbsenceTypes(_languageID)
    .then((result: IJustificationAbsenceTypesDto[]) => {
      const updatedOptions: SelectOption[] = result.map((item) => ({
        value: item.idTypeAbsence,
        label: item.absence,
      }));
      setOptionsJustificationAbsence(updatedOptions)
    })
    .finally(() => props.setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.setLoading]);

  const handlerSubmitUpdate = () => {

    try {
      const dataRequest: IUpdateJustificationAbsenceDto = {
        IDJustificationAbsence: formValuesUpdate.idJustificationAbsence,
        JustificationAbsenceTypeID: formValuesUpdate.justificationAbsenceType[0]
      }
  
      props.setLoadingModal(true);
      justificationAbsencesService.UpdateJustificationAbsenceType(dataRequest).then((result) => {
        
        const message = labelsResponseUpdateJustificationAbsence[result];
        props.setOpenModalMessage(true);
        props.setMessageModalMessage(message);
        props.setLoadingModal(false);
        
        if (result === 1) {

          setModalUpdateOpen(false);
          setFormValuesUpdate(initialFormUpdateValues);
          resetSearch();
          getJustificationAbsences(languageID.value);
        }
      });
    } catch (error) {

      console.log(error);
      props.setOpenModalError(true);
      props.setMessageModalError(t('modal.unexpectedError'));
      props.setLoadingModal(false);
    }
  }

  const handleDeleteJustificationAbsence = () => {

    try{

      props.setLoadingModal(true);
      justificationAbsencesService.DeleteJustificationAbsence(valueJustificationAbsenceDelete).then((result) => {

        const message = labelsResponseDeleteJustificationAbsence[result];
        props.setOpenModalMessage(true);
        props.setMessageModalMessage(message);
        props.setLoadingModal(false);
        
        if (result === 1) {

          setDeleteModal(false);
          setFormValuesUpdate(initialFormUpdateValues);
          resetSearch();
          getJustificationAbsences(languageID.value);
        }
      });

    } catch (error) {

      console.log(error);
      props.setOpenModalError(true);
      props.setMessageModalError(t('modal.unexpectedError'));
      props.setLoadingModal(false);
    }
  };

  const handleOnDateRangeChange = (newDate: any) => {
    let { startDate: newStartDate, endDate: newEndDate } = newDate;

    if ((newStartDate && dateOutsideRange(newStartDate)) || (newEndDate && dateOutsideRange(newEndDate)))
      return false;

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const dateOutsideRange = (date: MomentObj) => {
      
    if (!startDate || !startDate.isValid()) {
      return date.isAfter(moment(), "day");
    }
    const endDateLimitBefore = startDate.clone().add(1, "months");
    const validationEndDateLimitBefore =
      date.isBefore(startDate, "day") || date.isAfter(endDateLimitBefore, "day");
    return validationEndDateLimitBefore || date.isAfter(moment(), "day");
  }

  const onClickConsultInformation = async () => {

    if (!startDate || !endDate)
      return;
    
    getJustificationAbsences(languageID.value);
  }

  const getReportExcel = () => {
      
    const range = { startDate: startDate ?? initialRangeDate.startDate, endDate: endDate ?? initialRangeDate.endDate  }
    props.setLoadingModal(true);
    justificationAbsencesService.GetJustificationAbsencesReport(languageID.value, range.startDate.format("MM/DD/YYYY"), range.endDate.format("MM/DD/YYYY"))
    .then((r: Blob | any) => {
      try {
        if (r?.statusCode === 401) {
          props.setMessageModalError(t("modal.generalError"));
          props.setOpenModalError(true);
          console.error('Error:', "Download failed");
          return;
        } 
        
        try {
          const fileName = 'justification_absences';
          const url = window.URL.createObjectURL(r);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName + ".xlsx");
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error al descargar el archivo:", error);
        }
      } catch (error) {
        props.setMessageModalError(t("modal.generalError"));
        props.setOpenModalError(true);
      } finally {
        props.setLoadingModal(false);
      }
    });
  };

  useEffect(() => {
    
    getJustificationAbsences(languageID.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageID])
  
  useEffect(() => {
    
    getJustificationAbsenceTypes(languageID.value);
  }, [getJustificationAbsenceTypes, languageID])
  
  return {
    openModalHelp,
    setOpenModalHelp,
    paginationProps,
    columns,
    pagedData,
    tablePreferences,
    modalUpdateOpen,
    setModalUpdateOpen,
    formValuesUpdate,
    optionsJustificationAbsence,
    onChangeJustificationAbsenceUpdate,
    handlerSubmitUpdate,
    openDeleteModal,
    setDeleteModal,
    handleDeleteJustificationAbsence,
    startDate,
    endDate,
    onClickConsultInformation,
    handleOnDateRangeChange,
    dateOutsideRange,
    chartData,
    chartOptions,
    disabledBtnDownloadReport,
    getReportExcel
  };
}